import React from "react";
import {IntlShape} from "react-intl";
import {Link} from "react-router-dom";

import {ESearchQueries} from "../models";

import Highlighter from "@toolbox/display-blocks/Highlighter";
import {intl2Str} from "@translate/T";
import {
    renderCreateSopButton,
    renderImportButton,
    renderManageButton,
} from "../search/SearchButtons";
import ProtectedImage from "./ProtectedImage";

export function getTitle(
    name: string,
    id: number,
    highlighter: string = "",
    className: string = "d-flex",
    matches: string[] = [],
    customLogo: boolean,
) {
    const queryParts = highlighter.split(" ");
    const linkQuery =
        matches.length > 0 ||
        !queryParts.every((part) =>
            name.toLowerCase().includes(part.toLowerCase()),
        );

    const to = `/project/${id}` + (linkQuery ? `?q=${highlighter}` : "");
    return (
        <Link id={`project-link-${id}`} className={className} to={to}>
            <span className="text-dot-overflow">
                <Highlighter query={highlighter} text={name} />
            </span>
            {customLogo && (
                <span className="ml-auto">
                    <ProtectedImage
                        url={`/api/report/logo/${id}`}
                        height="1.25rem"
                    />
                </span>
            )}
        </Link>
    );
}

export function getSops(intl: IntlShape, sops: number, id: number) {
    let text = "";
    switch (sops) {
        case 1:
            text = intl2Str(intl, "1 SOP");
            break;

        default:
            text = intl2Str(intl, "{sops} SOPs", {sops});
            break;
    }

    return (
        <Link
            id={`sops-${id}`}
            to={`/project/${id}?q=${encodeURIComponent(ESearchQueries.Sops)}`}
        >
            {text}
        </Link>
    );
}

export function getMeasurements(
    intl: IntlShape,
    measurements: number,
    id: number,
) {
    let text = "";
    switch (measurements) {
        case 1:
            text = intl2Str(intl, "1 Measurement");
            break;

        default:
            text = intl2Str(intl, "{measurements} Measurements", {
                measurements,
            });
            break;
    }

    return (
        <Link
            id={`measurements-${id}`}
            to={`/project/${id}?q=${encodeURIComponent(
                ESearchQueries.Measurements,
            )}`}
        >
            {text}
        </Link>
    );
}

export function getAnalyses(intl: IntlShape, analyses: number, id: number) {
    let text = "";
    switch (analyses) {
        case 1:
            text = intl2Str(intl, "1 Analysis");
            break;

        default:
            text = intl2Str(intl, "{analyses} Analyses", {analyses});
            break;
    }

    return (
        <Link
            id={`analyses-${id}`}
            to={`/project/${id}?q=${encodeURIComponent(
                ESearchQueries.Analyses,
            )}`}
        >
            {text}
        </Link>
    );
}

export function getButtons(id: number, intl: IntlShape) {
    const _manage = renderManageButton(intl, id, " btn-sm", false);
    const _sop = renderCreateSopButton(id, "btn-sm", true);
    const _import = renderImportButton(intl, id, " btn-sm", false);

    if (!_manage && !_sop && !_import) {
        return null;
    }

    return (
        <React.Fragment>
            {_import}
            {_sop}
            {_manage}
        </React.Fragment>
    );
}

export function getMatches(matches: string[], query: string) {
    if (!matches || !matches.length) {
        return null;
    }

    return (
        <ul className="list-unstyled text-muted">
            {matches.map((match, i) => (
                <li key={i}>
                    {
                        <span
                            className="d-block text-dot-overflow"
                            onMouseEnter={abbreviationTitle}
                        >
                            <Highlighter query={query} text={match} />
                        </span>
                    }
                </li>
            ))}
        </ul>
    );
}

function abbreviationTitle(
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
) {
    const {currentTarget} = event;
    const {title, offsetWidth, scrollWidth, textContent} = currentTarget;
    if (title === "") {
        if (offsetWidth < scrollWidth) {
            currentTarget.title = textContent ?? "";
        }
    } else if (offsetWidth >= scrollWidth && title === textContent) {
        currentTarget.title = "";
    }
}
